import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import StatusPill from "../../Budgets/components/StatusPill";
import { fmtEur } from "./format";

// PILL_STATUS maps boardfigures.VerificationRow's status vocabulary ("verified" |
// "self_reported" - internal/boardfigures/savings.go's BuildSavingsVerification) onto
// StatusPill's own STATUS_CONFIG keys (src/views/Budgets/components/StatusPill.jsx), mirroring
// HorizonTableBlock's PILL_STATUS convention of remapping the existing pill vocabulary rather
// than inventing new pill styles: "verified" (independently billing-backed) -> "green" ("On
// track"); "self_reported" (no independent estimate yet) -> "at_risk" ("At risk").
const PILL_STATUS = { verified: "green", self_reported: "at_risk" };

function pillStatus(status) {
  return PILL_STATUS[status] || status;
}

const columns = [
  { Header: "Title", accessor: "title" },
  { Header: "Committed", accessor: "committed", align: "right", Cell: ({ value }) => fmtEur(value) },
  { Header: "Realized", accessor: "realized", align: "right", Cell: ({ value }) => fmtEur(value) },
  {
    Header: "Est. verified",
    accessor: "estimatedRealized",
    align: "right",
    Cell: ({ value }) => (value == null ? "—" : fmtEur(value)),
  },
  {
    Header: "Status",
    accessor: "status",
    align: "center",
    Cell: ({ value }) => <StatusPill status={pillStatus(value)} />,
  },
];

/**
 * SavingsVerificationTableBlock - Page 4's committed-vs-realized-vs-independently-verified
 * table (savings_verification_table block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/savings-verification ->
 * envelope key "savings_verification" -> boardfigures.SavingsVerification { rows:
 * [VerificationRow], summary: {...} } (internal/boardfigures/savings.go's
 * BuildSavingsVerification). Until the savings-verification bridge deploys, every row's
 * estimatedRealized is nil -> status "self_reported" and "—" renders - correct by design, not
 * a bug (see the plan's "Verification honesty" constraint). Money is EUR float (fmtEur
 * direct, no ÷100).
 */
const SavingsVerificationTableBlock = ({ title = "Committed vs realized vs verified", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/savings-verification`
  );

  const rows = data?.savings_verification?.rows ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-savings-verification">
      {rows.length > 0 ? (
        <FlexTable columns={columns} data={rows} getRowId={(row) => row.id} />
      ) : (
        // No committed/realized items yet: a calm note instead of bare column headers
        // (FlexTable always draws its header group even with zero data rows).
        <p className="a4-empty-note" style={{ color: "#6b6b6b", fontSize: "9pt", margin: "2mm 0" }}>
          No savings to verify yet.
        </p>
      )}
    </BaseBlock>
  );
};

SavingsVerificationTableBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SavingsVerificationTableBlock;
